// state/feature-flags.ts — W2.5 Zustand slice for resolved feature flags.
//
// Loaded once after authentication via `loadFeatureFlags()`. Components read
// individual flags through `useFeatureFlag(key)` (see ../hooks/useFeatureFlag).
//
// Fail-closed semantics: if the network call fails, the resolved map stays
// empty and `useFeatureFlag` returns false for every key — same behavior as
// "default off". Safer than fail-open in case the gating decision is "show
// only when flagged on".
//
// NOT persisted: re-loaded on every app boot so a flag flip on the server
// surfaces on next refresh without stale localStorage state.

import { create } from 'zustand';
import { flagsApi, type FlagState, type FlagSource } from '../api/flags';

interface FeatureFlagsState {
  /** Resolved key → state map. Empty until `loadFeatureFlags()` resolves. */
  flags: Record<string, FlagState>;
  /** Resolved key → source map (for debug / analytics). */
  sources: Record<string, FlagSource>;
  /** True after the first load attempt completes (success or failure). */
  loaded: boolean;
  load: () => Promise<void>;
  /** Manual override for tests. Not for production use. */
  setFlagsForTest: (flags: Record<string, FlagState>) => void;
}

export const useFeatureFlags = create<FeatureFlagsState>((set) => ({
  flags: {},
  sources: {},
  loaded: false,
  load: async () => {
    try {
      const list = await flagsApi.list();
      const flags: Record<string, FlagState> = {};
      const sources: Record<string, FlagSource> = {};
      for (const f of list) {
        flags[f.key] = f.state;
        sources[f.key] = f.source;
      }
      set({ flags, sources, loaded: true });
    } catch {
      // Fail-closed: leave flags empty, mark loaded so UI doesn't hang on a
      // perpetual "loading flags" state.
      set({ loaded: true });
    }
  },
  setFlagsForTest: (flags) => set({ flags, loaded: true }),
}));

/** Top-level loader. Safe to call multiple times — re-loads each time. */
export async function loadFeatureFlags(): Promise<void> {
  await useFeatureFlags.getState().load();
}
